import { isFeed7Id } from '../lib/feed7';
import { isPolyquantId } from '../lib/polyquant';
import { FEED7, FEED_KIND, POLYQUANT } from '../lib/feedContext';
import { isMainFeed2Id, PRIVATE_MIRROR, RU_FEED, TRANSFERS } from '../lib/feedContext';
/**
 * Из вилок — в строки экрана: расчёт, порядок, ярусы, хвост.
 *
 * Сортировка идёт по рублям, которые реально снимутся, а не по проценту. Замер
 * 02.08 объясняет почему: 27.69 % стоили 198 ₽, потому что в стакане лежало
 * шесть долларов, а 14.53 % стоили 1451 ₽ при глубине 472 $. Лента,
 * отсортированная по проценту, ставит наверх ровно то, что брать нельзя.
 *
 * Порядок замораживается, пока курсор над лентой или строка раскрыта, — приём
 * из биржевых блоттеров. Без него человек целится в одну строку, а нажимает в
 * другую: список успевает переехать за время движения руки.
 */

import { useDeferredValue, useMemo } from 'react';

import { type Fork, ageOf } from '../lib/protocol';
import { gateProfit } from './gate';
import { useShow, type ShowUnit } from './show';
import { HAND_SOURCE, type Row, type RowInput, type Tier, buildRow, tierOf, tooShort } from '../lib/row';
import { tempoOf, useSettings } from '../store/settings';
import { afterlifeVisible } from './afterlife';
import { type Stillness, stillness } from './still';
import { heldIds, type Ghost, useScreen } from './store';

/**
 * Потолка на число строк здесь нет, и это решение, а не упущение.
 *
 * Раньше тело обрезалось девятью строками, а хвост собирал только мелочь ниже
 * порога — всё, что не поместилось сверху, не попадало никуда. На боевом кадре
 * из 45 вилок это означало 9 на экране и 36 в никуда, без счётчика и без слова.
 * Пропажа без объяснения неотличима от «вилок нет»: человек листает пустоту и
 * уходит на чужой сканер, где их «12 216 788».
 *
 * Сколько строк крупные, решает порог оператора: ярус — это высота строки, а не
 * право быть на экране. Регулятор порога и есть та ручка, которой сокращают
 * ленту, и она в руках у человека, а не зашита числом в коде.
 */

export interface FeedRow {
  id: string;
  row: Row;
  age: number;
  tier: Tier;
  /** Появилась только что: строка вспыхивает и гаснет. */
  born: boolean;
  /**
   * Прибыль только что шевельнулась: `1` — выросла, `-1` — упала, `0` — стоит.
   *
   * Плата за спокойный порядок (`calm.ts`). Раньше «лента живая» читалось по
   * тому, что строки постоянно менялись местами; теперь они стоят, и без этой
   * пометки неподвижная лента выглядела бы замершей — а замершему сканеру
   * вилочник не верит и уходит обновлять страницу. Подсветка идёт от НАСТОЯЩЕЙ
   * дельты цены, а не от перерисовки: мигать на каждый кадр значит подделывать
   * оживление там, где ничего не произошло.
   */
  beat: -1 | 0 | 1;
  /** Может не дожить до второй ноги при измеренном темпе. */
  risky: boolean;
  /**
   * Залипшая нога: сколько стоит без движения самая застоявшаяся цена.
   *
   * Считалась и раньше, но жила ТОЛЬКО в раскрытой карточке. Строка при этом
   * писала «дольше обычного» — то есть хвалила ровно те вилки, про которые
   * карточка после нажатия говорила «похоже, вилки уже нет» (08.08, владелец:
   * «ощущение, что любую можно брать»). Решение «ставить или нет» принимается
   * в ленте, значит и довод обязан быть в ленте.
   */
  still: Stillness | null;
  /**
   * Хотя бы у одной ноги нет ссылки на контору. Не опасность, а работа: матч и
   * исход придётся искать руками, уже держа первую ногу.
   *
   * Ссылка в раздел каталога сюда НЕ входит, хотя действие у человека то же —
   * искать глазами. Считалась и она (тогда метка вставала на 183 строки из 438
   * вместо 90), но метка на половине ленты перестаёт быть меткой: 08.08
   * владелец вернул узкую границу. Точность входа по-прежнему названа словами
   * на самой ноге в карточке — см. `entryNote`.
   */
  linkless: boolean;
  /** Живучесть, по которой считается полоса: своя, если измерена, иначе групповая. */
  lifespan: number | null;
  ghost: Ghost | null;
}

/**
 * Насколько строке нельзя верить, числом — для сортировки.
 *
 * Один слой стопа: гейт сказал «цена неверна». Залипших больше не топим
 * отдельно — в «работе» их режет возраст строки или штраф `sv` в порядке
 * по деньгам, а каденция трубы (`stale` за 15 с) смертью не считается.
 */
export const distrust = (row: FeedRow): number => (row.row.doubt === 'dead' ? 2 : 0);

export interface FeedView {
  /** Строки от порога и выше — все, сколько есть. */
  big: FeedRow[];
  /** Строки ниже порога — тоже все: разделитель перед ними не прячет, а объясняет. */
  tail: FeedRow[];
  /** Сколько вилок пришло с сервера — до расчёта и порогов. */
  incoming: number;
  /**
   * Сколько живых вилок посчитать не удалось: нет курса, нет цен, или после
   * округления ставок прибыли не остаётся. Строки для них нет — рисовать нечего,
   * — но и молчать нельзя: с экрана это выглядит как «сервер их не нашёл».
   */
  unpriced: number;
  /**
   * Числа в строках посчитаны по прежним деньгам: банк или курс только что
   * изменили, а пересчёт ещё идёт. Флаг обязан доезжать до экрана — строка, не
   * отвечающая регулятору, выглядит исправной.
   */
  stale?: boolean;
}

const lifespanOf = (fork: Fork): number | null => {
  if (typeof fork.sv === 'number') return fork.sv;
  return typeof fork.svGroup === 'number' ? fork.svGroup : null;
};

interface Priced {
  fork: Fork;
  row: Row | null;
  lockKey: string;
}

/**
 * Кэш посчитанных строк. Вилка считается заново, только если изменилась сама
 * или если человек подвинул регуляторы.
 *
 * Замер 06.08.2026 на живом кадре второго контура: пересчёт 396 вилок стоит
 * 428 мс медианой, потому что на каждую идёт поиск объёма золотым сечением на
 * `Decimal`. Кадр пересобирается на каждую дельту, на секундный тик возраста и
 * на любую запись в стор — включая ту, что делает наведение мыши. Без кэша
 * подсветка строки ждала два таких пересчёта и отставала на секунды.
 *
 * Сравнение по ссылке на вилку, а не по её полям: клиент сокета правит одну и
 * ту же `Map` и подменяет объект только у той строки, которой пришла дельта.
 */
let priced = new Map<string, Priced>();
let pricedFor = '';

/**
 * Последняя замеченная прибыль строки и когда она изменилась. Живёт рядом с
 * кэшем расчёта и по той же причине: сравнивать кадр можно только с прошлым
 * кадром, а прошлого кадра у чистой функции нет.
 */
const beats = new Map<string, { profit: number; at: number; dir: -1 | 0 | 1 }>();

/** Сколько подсветка держится на экране, мс. */
const BEAT_MS = 700;
/**
 * Ниже этого шевеления подсветки нет. Полпроцента — граница шума: округление
 * ставок и копеечная правка курса меняют прибыль на десятые доли процента, и
 * подсветка на них горела бы не переставая, то есть не значила бы ничего.
 */
const BEAT_SHARE = 0.005;

const beatOf = (id: string, profit: number, now: number): -1 | 0 | 1 => {
  const was = beats.get(id);
  if (was === undefined) {
    // Первую встречу за шевеление не считаем: строка и без того помечена `born`.
    beats.set(id, { profit, at: 0, dir: 0 });
    return 0;
  }
  const moved = Math.abs(profit - was.profit) > Math.max(1, Math.abs(was.profit) * BEAT_SHARE);
  if (moved) {
    const dir = profit > was.profit ? 1 : -1;
    beats.set(id, { profit, at: now, dir });
    return dir;
  }
  return now - was.at < BEAT_MS ? was.dir : 0;
};

/** Забыть подсветку — для тестов: память модульная и переживает размонтирование. */
export function forgetBeats(): void {
  beats.clear();
}

const inputKey = (input: RowInput): string =>
  `${JSON.stringify(input.transferBudget)}|${input.feedKind ?? ''}|${input.bank}|${input.fx}|${input.tempo ?? ''}|${JSON.stringify(input.caps)}|${JSON.stringify(input.capsCur ?? {})}|${JSON.stringify(input.couponCaps ?? {})}|${JSON.stringify(input.freebets ?? {})}|${JSON.stringify(input.freebetsCur ?? {})}|${input.roundStep ?? 10}|${input.roundDir ?? 'down'}|${input.roundWho ?? 'book'}`;

/**
 * Собрать строки. Заморозка сохраняет прежний порядок и дописывает новичков в
 * конец: строка не имеет права прыгнуть под курсор, но и пропасть из ленты
 * из-за того, что на неё смотрят, тоже не должна.
 */
export function buildFeed(
  forks: Map<string, Fork>,
  ghosts: Map<string, Ghost>,
  bornAt: Map<string, number>,
  now: number,
  input: RowInput,
  keep: readonly string[],
  hold = false,
): FeedView {
  const rows = new Map<string, FeedRow>();
  let unpriced = 0;

  // Кэш кадра собирается заново, поэтому строки ушедших вилок из него выпадают
  // сами: отдельная уборка не нужна и не может отстать от ленты.
  const key = inputKey(input);
  const reuse = key === pricedFor ? priced : new Map<string, Priced>();
  const frame = new Map<string, Priced>();

  const add = (fork: Fork, ghost: Ghost | null): void => {
    const age = ageOf(fork, now);
    const known = reuse.get(fork.id);
    const lockKey =
      input.lock?.forkId === fork.id ? `${input.lock.source}\x1f${input.lock.form}` : '';
    const row =
      known !== undefined && known.fork === fork && known.lockKey === lockKey
        ? known.row
        : buildRow(fork, input);
    frame.set(fork.id, { fork, row, lockKey });
    if (row === null) {
      if (ghost === null) unpriced += 1;
      return;
    }
    const born = now - (bornAt.get(fork.id) ?? now) < 1400;
    rows.set(fork.id, {
      id: fork.id,
      row,
      age,
      tier: 'small',
      born,
      // Призрак не шевелится: его числа последние, и подсвечивать в них нечего.
      beat: ghost === null ? beatOf(fork.id, row.profit, now) : 0,
      risky: tooShort(fork, input.tempo),
      still: stillness(fork, now),
      linkless: row.legs.some((leg) => leg.url === null && leg.source !== HAND_SOURCE),
      lifespan: lifespanOf(fork),
      ghost,
    });
  };

  for (const fork of forks.values()) add(fork, null);
  // Призрак считается по своим последним числам: пересчитывать умершую вилку
  // текущим стаканом нечем, да и незачем — она уже не сделка, а объяснение.
  if (!['feed6', 'feed7'].includes(input.feedKind ?? FEED_KIND)) {
    for (const [id, ghost] of ghosts) if (!isPolyquantId(id) && !isFeed7Id(id) && !rows.has(id)) add(ghost.fork, ghost);
  }

  priced = frame;
  pricedFor = key;
  // Кэш расчёта собирается заново и чистится сам, а память подсветки живёт
  // между кадрами — её приходится убирать руками, иначе за смену она вырастет
  // на все вилки, что прошли через ленту.
  if (beats.size > frame.size * 2) for (const id of beats.keys()) if (!frame.has(id)) beats.delete(id);

  const ordered: FeedRow[] = [];
  const seen = new Set<string>();
  for (const id of keep) {
    const row = rows.get(id);
    if (row !== undefined) {
      ordered.push(row);
      seen.add(id);
      continue;
    }
    // CUPIS: срок серой строки задаёт store. Не воскрешаем её из кэша после
    // истечения GHOST_MS: курсор может оставаться над лентой часами. Рабочую
    // карточку store сохраняет сам; живые строки сохраняют порядок и цены.
    const last = hold && !/^exp:(live|prematch):/.test(id) && !isPolyquantId(id) && !isFeed7Id(id) && !['feed6', 'feed7'].includes(input.feedKind ?? FEED_KIND) && !PRIVATE_MIRROR && !RU_FEED ? lastSeen.get(id) : undefined;
    if (last !== undefined) {
      ordered.push(last.ghost !== null ? last : { ...last, ghost: { fork: last.row.fork, why: 'other', at: now } });
      seen.add(id);
    }
  }
  if (!hold || ['feed6', 'feed7'].includes(input.feedKind ?? FEED_KIND) || PRIVATE_MIRROR || RU_FEED) lastSeen.clear();
  for (const row of rows.values()) lastSeen.set(row.id, row);
  const fresh = [...rows.values()].filter((row) => !seen.has(row.id));
  // Сомнительные тонут и среди новичков: при замороженном порядке это
  // единственная сортировка, которую они проходят, и без неё свежая строка с
  // опечаткой конторы вставала бы наверх дописанного куска.
  // `id` замыкает сравнение: при равных деньгах порядок обхода `Map` меняется
  // от переподключений и патчей, и без последнего ключа дописанный кусок
  // тасовался бы сам собой на каждом кадре.
  fresh.sort(
    (a, b) =>
      distrust(a) - distrust(b) ||
      b.row.profit - a.row.profit ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  ordered.push(...fresh);

  return { big: ordered, tail: [], incoming: forks.size, unpriced };
}

/**
 * Набитое плечо и свой кэф пересчитывают только свою вилку. Класть их в
 * общий ключ кэша нельзя: каждый символ в поле убил бы 400 строк.
 */
export function rowWithLock(item: FeedRow, input: RowInput): FeedRow {
  if (
    input.lock?.forkId !== item.id &&
    input.odds?.forkId !== item.id &&
    input.fees?.forkId !== item.id &&
    input.curs?.forkId !== item.id &&
    input.free?.forkId !== item.id &&
    input.hand?.forkId !== item.id
  ) {
    return item;
  }
  return rowWithFork(item, item.row.fork, input);
}

/** Пересчитать одну вилку с уже покрашенной книгой / кэфом. */
export function rowWithFork(item: FeedRow, fork: Fork, input: RowInput): FeedRow {
  const next = buildRow(fork, {
    ...input,
    transferBudget: input.transferBudget ?? item.row.transferBudget,
  });
  return next === null ? item : { ...item, row: next };
}

/**
 * Набитое плечо и свой кэф пересчитывают только свою вилку. Класть их в
 * общий ключ кэша нельзя: каждый символ в поле убил бы 400 строк.
 */
export function applyLock(
  view: FeedView,
  forks: Map<string, Fork>,
  ghosts: Map<string, Ghost>,
  input: RowInput,
): FeedView {
  const ids = new Set<string>();
  if (input.lock !== undefined) ids.add(input.lock.forkId);
  if (input.odds !== undefined) ids.add(input.odds.forkId);
  if (input.fees !== undefined) ids.add(input.fees.forkId);
  if (input.curs !== undefined) ids.add(input.curs.forkId);
  if (input.free !== undefined) ids.add(input.free.forkId);
  if (input.hand !== undefined) ids.add(input.hand.forkId);
  if (ids.size === 0) return view;
  let next = view;
  for (const id of ids) {
    const fork = forks.get(id) ?? ghosts.get(id)?.fork;
    if (fork === undefined) continue;
    const priced = buildRow(fork, input);
    if (priced === null) continue;
    const patch = (list: FeedRow[]): FeedRow[] =>
      list.map((item) => (item.id === id ? { ...item, row: priced } : item));
    next = { ...next, big: patch(next.big), tail: patch(next.tail) };
  }
  return next;
}

/**
 * Разложить по ярусам. Ярус — высота строки; ни одна строка при этом не теряется.
 * `pinned` — ярусы на время заморозки: смена яруса под курсором — это смена
 * высоты и переезд за черту «мельче», та же строка уезжает из-под руки.
 */
export function splitTiers(
  rows: readonly FeedRow[],
  gate: number,
  unpriced = 0,
  pinned?: Map<string, FeedRow['tier']>,
  unit: ShowUnit = 'rub',
  bank = 10000,
): FeedView {
  const big: FeedRow[] = [];
  const tail: FeedRow[] = [];
  for (const row of rows) {
    // The main feed's profit gate must not bury prepared feed2 rows in the
    // collapsed tail. Their visibility is decided by the source pipeline.
    const tier = (FEED7 || POLYQUANT || isFeed7Id(row.id) || isPolyquantId(row.id) || isMainFeed2Id(row.id)) ? 'take'
      : pinned?.get(row.id) ?? (TRANSFERS ? 'take' : tierOf(gateProfit(row.row, unit, bank), gate));
    pinned?.set(row.id, tier);
    const placed = { ...row, tier };
    if (tier === 'small' && !RU_FEED) tail.push(placed);
    else big.push(placed);
  }
  return { big, tail, incoming: rows.length, unpriced };
}

const lastSeen = new Map<string, FeedRow>();
const frozenTiers = new Map<string, FeedRow['tier']>();

/** Строки для текущего кадра. Хук держит расчёт в одном месте для ленты и витрины. */
export function useRows(): FeedView {
  const forks = useScreen((s) => s.forks);
  const ghosts = useScreen((s) => s.ghosts);
  const bornAt = useScreen((s) => s.bornAt);
  const now = useScreen((s) => s.now);
  const frozen = useScreen((s) => s.frozen);
  const order = useScreen((s) => s.order);

  // Деньги идут в расчёт с отсрочкой, а порог и фильтры — сразу.
  //
  // Банк, курс, лимиты и округление — ручки, которые обесценивают кэш строк:
  // от них зависит объём, а значит и каждая цифра в строке. Банк при этом
  // вводится с клавиатуры, и без отсрочки каждый набранный символ вставал бы в
  // очередь перед полным пересчётом кадра — замер 06.08.2026 даёт на него
  // 450 мс при четырёх сотнях вилок. Отложенное значение отдаёт нажатие экрану
  // сразу, а пересчёт уводит в фоновую перерисовку. Округление — один клик,
  // его читаем сразу: иначе после «применить» кэш отдал бы старые суммы.
  //
  // Плата за это — доли секунды, когда в поле уже новый банк, а в строках ещё
  // старые числа. Молчать об этом нельзя: цифры, не отвечающие регулятору, это
  // ровно тот случай, когда сканер врёт, не сломавшись. Поэтому расхождение
  // называется вслух — `stale` ниже.
  const transferBudget = useSettings((s) => TRANSFERS ? s.transferBudget : undefined);
  const bankAsked = useSettings((s) => s.bank);
  const fxAsked = useSettings((s) => s.fx);
  const capsAsked = useSettings((s) => s.caps);
  const capsCur = useSettings((s) => s.capsCur);
  const couponCaps = useSettings((s) => s.couponCaps);
  const freebetsAsked = useSettings((s) => s.freebets);
  const freebetsCur = useSettings((s) => s.freebetsCur);
  const roundStep = useSettings((s) => s.roundStep);
  const roundDir = useSettings((s) => s.roundDir);
  const roundWho = useSettings((s) => s.roundWho);
  const bank = useDeferredValue(bankAsked);
  const fx = useDeferredValue(fxAsked);
  const caps = useDeferredValue(capsAsked);
  const freebets = useDeferredValue(freebetsAsked);
  const stale = bank !== bankAsked || fx !== fxAsked || caps !== capsAsked || freebets !== freebetsAsked;
  const lock = useScreen((s) => s.stakeLock);
  const odds = useScreen((s) => s.oddsLock);
  const fees = useScreen((s) => s.feeLock);
  const curs = useScreen((s) => s.curLock);
  const free = useScreen((s) => s.freeLock);
  const hand = useScreen((s) => s.handLock);

  const gate = useSettings((s) => s.gate);
  const unit = useShow((s) => s.unit);
  const attempts = useSettings((s) => s.attempts);
  const open = useScreen((s) => s.open);
  const popped = useScreen((s) => s.popped);
  const steps = useScreen((s) => s.steps);
  const hiddenForks = useScreen((s) => s.hiddenForks);

  // Кадр держится в памяти хука: выделение строки, раскрытие карточки и любая
  // другая запись в стор перерисовывают ленту, но пересобирать её при этом
  // незачем — данные те же. Без этого наведение мыши стоило полного пересчёта.
  const view = useMemo(() => {
    const input = {
      transferBudget,
      bank,
      fx,
      caps,
      capsCur,
      couponCaps,
      freebets,
      freebetsCur,
      tempo: tempoOf(attempts),
      roundStep,
      roundDir,
      roundWho,
      lock: lock ?? undefined,
      odds: odds ?? undefined,
      fees: fees ?? undefined,
      curs: curs ?? undefined,
      free: free ?? undefined,
      hand: hand ?? undefined,
    };
    // Вне заморозки порядок держит `settle` в фильтрах. Своя пересортировка раз в
    // 2 с дописывала новые вниз и потом перекидывала их — лента дёргалась (28.09).
    const feed = applyLock(
      buildFeed(forks, ghosts, bornAt, now, input, frozen ? order : [], frozen),
      forks,
      ghosts,
      input,
    );
    if (!frozen) frozenTiers.clear();
    const held = heldIds({ steps, open, popped, hiddenForks });
    const keep = (rows: FeedRow[]): FeedRow[] =>
      rows.filter(
        (row) => !hiddenForks.has(row.id) && afterlifeVisible(row.id, row.row.fork, held),
      );
    const split = splitTiers(feed.big, gate, feed.unpriced, frozen ? frozenTiers : undefined, unit, bank);
    return { ...split, big: keep(split.big), tail: keep(split.tail) };
  }, [forks, ghosts, bornAt, now, transferBudget, bank, fx, caps, capsCur, couponCaps, freebets, freebetsCur, roundStep, roundDir, roundWho, attempts, frozen, order, gate, unit, lock, odds, fees, curs, free, hand, steps, open, popped, hiddenForks]);

  return stale ? { ...view, stale } : view;
}

/**
 * Строки раздела «скрытые». Считаются отдельно от кадра ленты: общий кэш
 * `buildFeed` нельзя делить на два списка, а спрятанных мало — по числу нажатий.
 */
export function useHiddenRows(): FeedRow[] {
  const hiddenForks = useScreen((s) => s.hiddenForks);
  const forks = useScreen((s) => s.forks);
  const now = useScreen((s) => s.now);
  const transferBudget = useSettings((s) => TRANSFERS ? s.transferBudget : undefined);
  const bank = useSettings((s) => s.bank);
  const fx = useSettings((s) => s.fx);
  const caps = useSettings((s) => s.caps);
  const capsCur = useSettings((s) => s.capsCur);
  const couponCaps = useSettings((s) => s.couponCaps);
  const freebets = useSettings((s) => s.freebets);
  const freebetsCur = useSettings((s) => s.freebetsCur);
  const attempts = useSettings((s) => s.attempts);
  const roundStep = useSettings((s) => s.roundStep);
  const roundDir = useSettings((s) => s.roundDir);
  const roundWho = useSettings((s) => s.roundWho);
  const lock = useScreen((s) => s.stakeLock);
  const odds = useScreen((s) => s.oddsLock);
  const fees = useScreen((s) => s.feeLock);
  const curs = useScreen((s) => s.curLock);
  const free = useScreen((s) => s.freeLock);
  const hand = useScreen((s) => s.handLock);

  return useMemo(() => {
    const input = {
      transferBudget,
      bank,
      fx,
      caps,
      capsCur,
      couponCaps,
      freebets,
      freebetsCur,
      tempo: tempoOf(attempts),
      roundStep,
      roundDir,
      roundWho,
      lock: lock ?? undefined,
      odds: odds ?? undefined,
      fees: fees ?? undefined,
      curs: curs ?? undefined,
      free: free ?? undefined,
      hand: hand ?? undefined,
    };
    const rows: FeedRow[] = [];
    for (const [id, hid] of hiddenForks) {
      const live = forks.get(id);
      const fork = live ?? hid.fork;
      const ghost =
        live !== undefined && hid.why === null
          ? null
          : { fork, why: hid.why ?? 'other', at: hid.at };
      const row = buildRow(fork, input);
      if (row === null) continue;
      rows.push({
        id,
        row,
        age: ageOf(fork, now),
        tier: 'small',
        born: false,
        beat: 0,
        risky: false,
        still: null,
        linkless: row.legs.some((leg) => leg.url === null && leg.source !== HAND_SOURCE),
        lifespan: lifespanOf(fork),
        ghost,
      });
    }
    return rows;
  }, [hiddenForks, forks, now, transferBudget, bank, fx, caps, capsCur, couponCaps, freebets, freebetsCur, attempts, roundStep, roundDir, roundWho, lock, odds, fees, curs, free, hand]);
}
